import React from 'react'
import { Table, Th, Td, Tr } from '@/shared/ui/Table'
import { Button, Input, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'
import type { Plan } from '../types'
import { PLAN_INTERVAL_LABEL } from '../types'

type Props = {
  items: Plan[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  search: string
  onSearchChange: (v: string) => void
  onCreate: () => void
  onEdit: (p: Plan) => void
  onDelete: (p: Plan) => void
}

export default function PlansTable({ items, total, page, pageSize, onPageChange, search, onSearchChange, onCreate, onEdit, onDelete }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const [q, setQ] = React.useState(search)
  React.useEffect(() => setQ(search), [search])
  React.useEffect(() => {
    const id = setTimeout(() => { if (q !== search) onSearchChange(q) }, 400)
    return () => clearTimeout(id)
  }, [q])

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar planos" className="pl-9" />
        </div>
        <Button onClick={onCreate}>Novo plano</Button>
      </div>

      <Table>
        <thead>
          <Tr>
            <Th>Nome</Th>
            <Th>Preço</Th>
            <Th>Intervalo</Th>
            <Th>Visível</Th>
            <Th>Status</Th>
            <Th className="w-12 text-right">Ações</Th>
          </Tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <Tr key={p.id}>
              <Td>{p.name}</Td>
              <Td>{(p.amount_cents/100).toLocaleString('pt-BR',{ style:'currency', currency:p.currency.toUpperCase()})}</Td>
              <Td>{PLAN_INTERVAL_LABEL[p.interval as keyof typeof PLAN_INTERVAL_LABEL] ?? p.interval}</Td>
              <Td>{p.visible ? <Badge variant="soft" color="success">Sim</Badge> : <Badge variant="soft" color="error">Não</Badge>}</Td>
              <Td>{p.active ? <Badge variant="soft" color="success">Ativo</Badge> : <Badge variant="soft" color="error">Inativo</Badge>}</Td>
              <Td className="text-right">
                <DropdownMenu
                  align="end"
                  trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Editar', onSelect: () => onEdit(p) },
                    { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(p) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {items.length === 0 && (
            <Tr>
              <Td colSpan={6} className="py-10 text-center text-sm text-foreground/70">Nenhum plano encontrado.</Td>
            </Tr>
          )}
        </tbody>
      </Table>

      <div className="mt-4 flex justify-end text-sm text-foreground/70">Página {page} de {totalPages}</div>
    </div>
  )
}

